import { useState, type MouseEvent } from 'react';
import { useQueryClient, UseQueryOptions } from '@tanstack/react-query';

import Linkv2 from '@/components/content/link/Linkv2';
import SeeMoreLink from '@/components/content/link/SeeMoreLink';
import DrawerLink from './DrawerLink';
import SidePanelContent from '@/components/content/sidepanel/SidePanelContent';

import { useToast } from '@/hooks/useToast';
import { useGetDrawerData } from './services/get';

import type { Module } from '../issue/types/modulesv2';
import { FetchError } from '@/types/api';
import { CollectorType } from '@/client/features/signals/types';

interface DeferredDrawerLinkProps<TData = unknown, TSelect = TData, TPreloadedData = TSelect> {
  value: string;
  title?: string;
  query: UseQueryOptions<TData, FetchError, TSelect>;
  content: Array<Module<TSelect>>;
  link?: string;
  noPrint?: boolean;
  id: number | string;
  preloadedData?: TPreloadedData;
}

interface CollectorData {
  collector: string;
}

const DeferredDrawerLink = <TData = unknown, TSelectData = TData, TPreloadedData = TSelectData>({
  value,
  title,
  content,
  query,
  link,
  id,
  noPrint,
  preloadedData,
}: DeferredDrawerLinkProps<TData, TSelectData, TPreloadedData>) => {
  const collectorsWithoutSeeMoreLink = new Set([
    CollectorType.RansomPage,
    CollectorType.RansomWatch,
    CollectorType.ViewRansomware,
  ]);
  const [enabled, setEnabled] = useState(false);
  const queryClient = useQueryClient();
  const { createToast } = useToast();

  const drawerQuery = preloadedData
    ? {
        data: preloadedData,
        isSuccess: true,
        isError: false,
        isLoading: false,
        error: null,
      }
    : useGetDrawerData(query, enabled);

  const data = drawerQuery?.data as CollectorData;

  if ((preloadedData && enabled) || (!preloadedData && drawerQuery.isSuccess && enabled)) {
    const shouldShowSeeMoreLink =
      !!link && !collectorsWithoutSeeMoreLink.has(data?.collector as CollectorType);

    return (
      <DrawerLink
        id={id}
        value={value}
        title={title}
        noPrint={noPrint}
        url={link}
        link={shouldShowSeeMoreLink ? <SeeMoreLink to={link} /> : null}
        content={
          <SidePanelContent
            modules={content}
            data={(preloadedData as TSelectData) ?? (drawerQuery.data as TSelectData)}
            id={id}
          />
        }
        initialOpen
      />
    );
  }

  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    setEnabled(true);

    if (drawerQuery.isError && drawerQuery.error) {
      createToast(drawerQuery.error.message, 'warning');
    }
  };

  const prefetchCatalog = () => {
    if (preloadedData || drawerQuery.isError) return;

    queryClient.prefetchQuery(query);
  };

  return (
    <Linkv2
      onMouseEnter={prefetchCatalog}
      onClick={onClick}
      href={link}
      disabled={drawerQuery.isLoading && enabled}
      animate={drawerQuery.isLoading && enabled}
      noPrint={noPrint}
    >
      {value}
    </Linkv2>
  );
};

export default DeferredDrawerLink;
